export enum ThoughtStepKey {
  TOOLS = "tools",
  CONTEXT = "context",
  RERANK = "rerank",
  STRICT = "strict",
  PLAN = "plan",
  DETECT_LANGUAGE = "detect_language",
  THINKING = "thinking",
  PREREQS = "prereqs",
  IMAGES = "images",
  ANALYSIS = "analysis",
  ADDITIONAL_CONTEXT = "additional_context",
  TOPIC = "topic",
}

export enum SourceType {
  INSTRUCTION = "instruction",
  BRIEF = "brief",
  TICKET = "ticket",
  WIKI = "wiki",
  ARTICLE = "article",
}

export interface KBContextItem {
  id: string;
  score: number;
  values: unknown[];
  metadata: {
    body: string;
    brand: string;
    draft: boolean;
    html_url: string;
    id: number;
    text: string;
    title: string;
    indexName: string;
  };
}

export interface TicketContextItem {
  id: string;
  score: number;
  values: unknown[];
  metadata: {
    answer: string;
    id: number;
    isGenerated: boolean;
    positiveFeedback: number;
    question: string;
    simulatorId: number;
    updatedAt: string;
    indexName: string;
  };
}

export interface WikiContextItem {
  id: string;
  score: number;
  values: unknown[];
  metadata: {
    brand: string;
    html_url: string;
    id: number;
    text: string;
    title: string;
  };
}

export interface ResourceQuote {
  source: SourceType;
  sourceId?: string;
  title?: string;
  quote: string;
}

export interface Contradiction {
  description: string;
  sources: Array<{
    source: SourceType;
    sourceId?: string;
    title?: string;
    quote: string;
  }>;
}

export interface ToolsStep {
  stepKey: ThoughtStepKey.TOOLS;
  allowedTools: string[];
  unavailable?: Array<{
    toolName: string;
    missingKeys: string[];
  }>;
}

export interface ContextStep {
  stepKey: ThoughtStepKey.CONTEXT;
  allowedSources: string[];
  userQuery?: string;
  contextData: {
    knowledgeRawContext?: KBContextItem[];
    ticketsRawContext?: TicketContextItem[];
    wikiKnowledgeContext?: WikiContextItem[];
    perplexityResponse?: unknown;
  };
}

export interface RerankStep {
  stepKey: ThoughtStepKey.RERANK;
  rerankResults?: {
    context?: unknown[];
    sources: {
      kb?: KBContextItem[];
      tickets?: TicketContextItem[];
      wiki?: WikiContextItem[];
    };
    contextExport?: unknown[];
    isKnowledgeExist?: boolean;
  };
}

export interface StrictStep {
  stepKey: ThoughtStepKey.STRICT;
  isStrictMode: boolean;
  isAllowed: boolean;
}

export interface PlanStep {
  stepKey: ThoughtStepKey.PLAN;
  planContent?: string;
}

export interface DetectLanguageStep {
  stepKey: ThoughtStepKey.DETECT_LANGUAGE;
  detectedLanguage: string;
}

export interface ThinkingStep {
  stepKey: ThoughtStepKey.THINKING;
  isThinkingNeeded: boolean;
  reason: string;
}

export interface PrereqsStep {
  stepKey: ThoughtStepKey.PREREQS;
  prerequisites: {
    isAllPrerequisitesMet: boolean;
    prerequisitesProvided: string[];
    prerequisitesNotProvided?: string[];
    reason: string;
  };
}

export interface ImagesStep {
  stepKey: ThoughtStepKey.IMAGES;
  processedImages: {
    images: Array<{
      image: string;
      description: string;
    }>;
  };
}

export interface AnalysisStep {
  stepKey: ThoughtStepKey.ANALYSIS;
  xmlPlan: string;
  userQueryExplanation: string;
  plan: string;
  resourceQuotes: ResourceQuote[];
  confidenceScore: number;
  keyDecisionPoints: string[];
  sourceContradictions: Contradiction[];
}

export type ThoughtStepValue =
  | ToolsStep
  | ContextStep
  | RerankStep
  | StrictStep
  | PlanStep
  | DetectLanguageStep
  | ThinkingStep
  | PrereqsStep
  | ImagesStep
  | AnalysisStep;

export interface AiResponseThoughtStep {
  id: string;
  createdAt: Date;
  updatedAt: Date;
  responseId: string;
  order: number;
  stepKey: ThoughtStepKey;
  value: ThoughtStepValue;
  durationMs?: number;

  tickets?: string[];
  releaseConversations?: number[];
  articles?: string[];
}

export interface AiResponseThought {
  id: string;
  createdAt: Date;
  updatedAt: Date;
  tenantId: string;

  releaseCommentId?: number;
  releaseConversationId?: number;
  suggestedReplyId?: number;
  releaseId?: number;
  simulatorId?: number;
  topicReleasePolicyId?: string;

  steps: AiResponseThoughtStep[];
}

export interface GetChainOfThoughtsRequest {
  ticketId?: string;
  releaseId?: number;
  simulatorId?: number;
  includeSteps?: boolean;
}

export interface GetChainOfThoughtsResponse {
  thoughts: AiResponseThought[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}

export interface ThoughtStepFilter {
  stepKey?: ThoughtStepKey;
  minDuration?: number;
  maxDuration?: number;
  hasTickets?: boolean;
  hasArticles?: boolean;
}

export interface ThoughtAnalysis {
  totalSteps: number;
  averageDuration: number;
  stepDistribution: Record<ThoughtStepKey, number>;
  confidenceScores: number[];
  keyDecisions: string[];
  contradictions: Contradiction[];
}

// AI Agent Step Types - representing the raw step format from API
export interface AiAgentToolCall {
  id: string;
  type: string;
  function: {
    name: string;
    arguments: string;
  };
}

export interface AiAgentToolResult {
  tool_call_id: string;
  content: string;
}

export interface AiAgentStepBase {
  text: string;
  files: unknown[];
  usage: {
    totalTokens: number;
    promptTokens: number;
    completionTokens: number;
  };
  request: {
    body: string;
  };
  sources: unknown[];
  response: unknown;
  stepType: string;
  warnings: unknown[];
  isContinued: boolean;
  finishReason: string;
  providerMetadata: Record<string, unknown>;
  reasoningDetails: unknown[];
}

export interface AiAgentStep extends AiAgentStepBase {
  toolCalls?: AiAgentToolCall[];
  toolResults?: AiAgentToolResult[];
}

export interface SuggestedReplyToolCall {
  toolName: string;
  toolCallId: string;
  args?: Record<string, unknown>;
  result?: unknown;
  type?: string;
}
